import React, { useRef, useState } from "react";
import { Add, DataObject, DeleteOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  createWorkflowKeyValue,
  createWorkflowResponseCapture,
  isValidWorkflowBodyTemplate,
  WORKFLOW_CHIP_PATTERN,
  workflowMethodSendsBody,
  WorkflowKeyValue,
  WorkflowResponseCapture,
  WorkflowWebServiceConfig,
} from "./workflowBuilderData";
import { WorkflowVariableOption } from "./workflowGraphMapper";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";
import WorkflowWebServicePreview from "./WorkflowWebServicePreview";

interface WorkflowWebServicePanelProps {
  config: WorkflowWebServiceConfig;
  /** The variables this stage's {{chips}} can use (see workflowVariablesBefore). */
  variables: WorkflowVariableOption[];
  onChange: (config: WorkflowWebServiceConfig) => void;
}

const SECRET_PREFIX = "secret:";

type TemplateChip = { token: string; kind: "variable" | "secret" | "unknown" };

/** The chips a URL or body uses, each marked a known variable, a secret, or unknown (a typo). */
const templateChips = (text: string, variables: WorkflowVariableOption[]): TemplateChip[] => {
  const known = new Set(variables.map((variable) => variable.key.toLowerCase()));
  const tokens = Array.from(new Set(Array.from(text.matchAll(WORKFLOW_CHIP_PATTERN), (match) => match[1])));
  return tokens.map((token) => ({
    token,
    kind: token.toLowerCase().startsWith(SECRET_PREFIX)
      ? "secret"
      : known.has(token.toLowerCase())
        ? "variable"
        : "unknown",
  }));
};

const URL_CHIPS_ID = "web-service-url-chips";
const BODY_CHIPS_ID = "web-service-body-chips";
export const BODY_INVALID_MESSAGE = "Not valid JSON. Put each {{variable}} inside quotes.";

type TemplateTarget = "url" | "body";

const WorkflowWebServicePanel: React.FC<WorkflowWebServicePanelProps> = ({
  config,
  variables,
  onChange,
}) => {
  const viewOnly = useWorkflowReadOnly();
  const inputRefs = { url: useRef<HTMLInputElement>(null), body: useRef<HTMLTextAreaElement>(null) };
  const [variableMenu, setVariableMenu] = useState<{ anchor: HTMLElement; target: TemplateTarget } | null>(
    null,
  );
  const body = config.body ?? "";
  const urlChips = templateChips(config.url, variables);
  const bodyChips = templateChips(body, variables);
  const bodyValid = isValidWorkflowBodyTemplate(body);

  // Inserts the chip at the caret (or over the selection), then puts the caret after it.
  const insertVariable = (key: string) => {
    if (!variableMenu) return;
    const { target } = variableMenu;
    setVariableMenu(null);
    const input = inputRefs[target].current;
    const text = target === "url" ? config.url : body;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? start;
    // In a JSON body a chip must sit inside a string; quote it unless the caret already is.
    const insideString = target === "body" && (text.slice(0, start).match(/"/g)?.length ?? 0) % 2 === 1;
    const chip = target === "body" && !insideString ? `"{{${key}}}"` : `{{${key}}}`;
    onChange({ ...config, [target]: text.slice(0, start) + chip + text.slice(end) });
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + chip.length, start + chip.length);
    });
  };

  const renderInsertButton = (target: TemplateTarget, ariaLabel?: string) =>
    viewOnly ? null : (
      <Button
        className="workflow-canvas-dialog__insert-variable"
        startIcon={<DataObject />}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={variableMenu?.target === target ? "true" : undefined}
        onClick={(event) => setVariableMenu({ anchor: event.currentTarget, target })}
      >
        Insert variable
      </Button>
    );

  const renderChips = (chips: TemplateChip[], id: string, label: string) =>
    chips.length === 0 ? null : (
      <ul id={id} className="workflow-canvas-dialog__url-chips" aria-label={label}>
        {chips.map((chip) => (
          <li key={chip.token}>
            <Chip
              size="small"
              className={chip.kind === "unknown" ? "workflow-canvas-dialog__url-chip--unknown" : undefined}
              label={
                chip.kind === "secret"
                  ? `Secret: ${chip.token.slice(SECRET_PREFIX.length).trim()}`
                  : chip.kind === "unknown"
                    ? `${chip.token}: not a workflow variable`
                    : chip.token
              }
            />
          </li>
        ))}
      </ul>
    );
  const updateKeyValue = (
    collection: "params" | "headers",
    id: string,
    patch: Partial<WorkflowKeyValue>,
  ) => {
    onChange({
      ...config,
      [collection]: config[collection].map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    });
  };

  const removeKeyValue = (collection: "params" | "headers", id: string) => {
    onChange({
      ...config,
      [collection]: config[collection].filter((item) => item.id !== id),
    });
  };

  const updateCapture = (id: string, patch: Partial<WorkflowResponseCapture>) => {
    onChange({
      ...config,
      captures: config.captures.map((capture) =>
        capture.id === id ? { ...capture, ...patch } : capture,
      ),
    });
  };

  const renderKeyValueRows = (collection: "params" | "headers") =>
    config[collection].map((item) => (
      <Box className="workflow-canvas-dialog__key-value" key={item.id}>
        <TextField
          disabled={viewOnly}
          label="Key"
          size="small"
          value={item.key}
          onChange={(event) => updateKeyValue(collection, item.id, { key: event.target.value })}
        />
        <TextField
          disabled={viewOnly}
          label="Value"
          size="small"
          value={item.value}
          onChange={(event) =>
            updateKeyValue(collection, item.id, { value: event.target.value })
          }
        />
        {!viewOnly && (
          <Tooltip title={`Remove ${collection === "params" ? "parameter" : "header"}`}>
            <IconButton
              aria-label={`Remove ${collection === "params" ? "parameter" : "header"}`}
              onClick={() => removeKeyValue(collection, item.id)}
            >
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ));

  return (
    <Box className="workflow-canvas-dialog__config-panel">
      <Alert severity="info">
        A web-service call lets the workflow talk to another Hamaspik system automatically. Use a
        Conditions canvas after this one to branch on a captured success value.
      </Alert>

      <Box className="workflow-canvas-dialog__config-grid">
        <FormControl disabled={viewOnly} size="small" fullWidth>
          <InputLabel id="web-service-method-label">Method</InputLabel>
          <Select
            labelId="web-service-method-label"
            label="Method"
            value={config.method}
            onChange={(event: SelectChangeEvent) =>
              onChange({
                ...config,
                method: event.target.value as WorkflowWebServiceConfig["method"],
              })
            }
          >
            {["GET", "POST", "PUT", "DELETE"].map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl disabled={viewOnly} size="small" fullWidth>
          <InputLabel id="web-service-failure-label">On failure</InputLabel>
          <Select
            labelId="web-service-failure-label"
            label="On failure"
            value={config.onFailure}
            onChange={(event: SelectChangeEvent) =>
              onChange({
                ...config,
                onFailure: event.target.value as WorkflowWebServiceConfig["onFailure"],
              })
            }
          >
            <MenuItem value="continue">Continue to next</MenuItem>
            <MenuItem value="cancel">Cancel the case</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box className="workflow-canvas-dialog__url">
        <Box className="workflow-canvas-dialog__url-row">
          <TextField
            disabled={viewOnly}
            label="URL"
            size="small"
            fullWidth
            required
            inputRef={inputRefs.url}
            value={config.url}
            onChange={(event) => onChange({ ...config, url: event.target.value })}
            helperText="Insert a {{variable}} to use a workflow value, or {{secret:Name}} for a stored secret."
            inputProps={urlChips.length > 0 ? { "aria-describedby": URL_CHIPS_ID } : undefined}
          />
          {renderInsertButton("url")}
        </Box>
        {renderChips(urlChips, URL_CHIPS_ID, "Variables in the URL")}
      </Box>

      {/* Only POST/PUT send a body; one typed under another method is kept, not shown. */}
      {workflowMethodSendsBody(config.method) && (
        <Box className="workflow-canvas-dialog__url">
          <Box className="workflow-canvas-dialog__url-row">
            <TextField
              disabled={viewOnly}
              label="Body (JSON)"
              size="small"
              fullWidth
              multiline
              minRows={3}
              inputRef={inputRefs.body}
              value={body}
              onChange={(event) => onChange({ ...config, body: event.target.value })}
              error={!bodyValid}
              helperText={
                bodyValid
                  ? "Values are JSON-escaped when the call is made, so keep each {{variable}} inside quotes."
                  : BODY_INVALID_MESSAGE
              }
              inputProps={bodyChips.length > 0 ? { "aria-describedby": BODY_CHIPS_ID } : undefined}
            />
            {renderInsertButton("body", "Insert variable into the body")}
          </Box>
          {renderChips(bodyChips, BODY_CHIPS_ID, "Variables in the body")}
        </Box>
      )}

      <Menu anchorEl={variableMenu?.anchor} open={Boolean(variableMenu)} onClose={() => setVariableMenu(null)}>
        {variables.length === 0 ? (
          <MenuItem disabled>No variables yet. Add form fields or captures to earlier stages.</MenuItem>
        ) : (
          variables.map((variable) => (
            <MenuItem key={variable.key} onClick={() => insertVariable(variable.key)}>
              <ListItemText primary={`{{${variable.key}}}`} secondary={variable.source} />
            </MenuItem>
          ))
        )}
      </Menu>

      <Box className="workflow-canvas-dialog__config-section">
        <Typography component="h3">Parameters</Typography>
        {renderKeyValueRows("params")}
        <Button
          disabled={viewOnly}
          className="workflow-canvas-dialog__mini-add"
          startIcon={<Add />}
          onClick={() =>
            onChange({ ...config, params: [...config.params, createWorkflowKeyValue()] })
          }
        >
          Add parameter
        </Button>
      </Box>

      <Box className="workflow-canvas-dialog__config-section">
        <Typography component="h3">Headers</Typography>
        {renderKeyValueRows("headers")}
        <Button
          disabled={viewOnly}
          className="workflow-canvas-dialog__mini-add"
          startIcon={<Add />}
          onClick={() =>
            onChange({ ...config, headers: [...config.headers, createWorkflowKeyValue()] })
          }
        >
          Add header
        </Button>
      </Box>

      <Box className="workflow-canvas-dialog__config-section">
        <Typography component="h3">Captures — response to variable</Typography>
        {config.captures.map((capture) => (
          <Box className="workflow-canvas-dialog__key-value" key={capture.id}>
            <TextField
              disabled={viewOnly}
              label="Response field"
              size="small"
              value={capture.responseField}
              onChange={(event) =>
                updateCapture(capture.id, { responseField: event.target.value })
              }
            />
            <TextField
              disabled={viewOnly}
              label="Workflow variable"
              size="small"
              value={capture.variable}
              onChange={(event) => updateCapture(capture.id, { variable: event.target.value })}
            />
            {!viewOnly && (
              <Tooltip title="Remove capture">
                <IconButton
                  aria-label="Remove capture"
                  onClick={() =>
                    onChange({
                      ...config,
                      captures: config.captures.filter((item) => item.id !== capture.id),
                    })
                  }
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
        <Button
          disabled={viewOnly}
          className="workflow-canvas-dialog__mini-add"
          startIcon={<Add />}
          onClick={() =>
            onChange({
              ...config,
              captures: [...config.captures, createWorkflowResponseCapture()],
            })
          }
        >
          Add capture
        </Button>
      </Box>

      {/* The dry run is a Builder CanEdit call (it can have side effects on the remote), so it is
          not offered in view-only mode. */}
      {!viewOnly && <WorkflowWebServicePreview config={config} />}
    </Box>
  );
};

export default WorkflowWebServicePanel;
